import { z } from 'zod';
/**
 * Meeting (Beratung 10–13): the host opens the room (lobby), starts the conversation (with the recording when the
 * meeting's event type records, D-162), ends it; LiveKit's `room_finished` ends it as well (`finish`, automatic);
 * the processing chain (recording import, transcription, summary draft — or the summary step alone without a
 * recording) marks it `processed`.
 */
export const meetingMachine = {
    name: 'meeting',
    states: ['scheduled', 'lobby', 'running', 'ended', 'processed'],
    labels: {
        scheduled: 'Geplant',
        lobby: 'Lobby',
        running: 'Läuft',
        ended: 'Beendet',
        processed: 'Rückblick bereit',
    },
    transitions: [
        { name: 'open', label: 'Raum öffnen', from: ['scheduled'], to: 'lobby' },
        { name: 'start', label: 'Gespräch starten', from: ['scheduled', 'lobby'], to: 'running' },
        { name: 'end', label: 'Beenden', from: ['lobby', 'running'], to: 'ended' },
        { name: 'finish', label: 'Beenden', from: ['lobby', 'running'], to: 'ended', automatic: true },
        { name: 'process', label: 'Rückblick bereit', from: ['ended'], to: 'processed', automatic: true },
    ],
};
// --- consent (Beratung 02, 10) -----------------------------------------------------------------------------
export const meetingConsentKeys = ['recording', 'transcript', 'storage', 'terms'];
/** The four items of the lobby card „Einwilligung · Pflicht", in the order of the screen. */
export const meetingConsentLabels = {
    recording: 'Aufzeichnung von Bild und Ton',
    transcript: 'Transkription des Gesprächs',
    storage: 'Speicherung in der Kontaktakte',
    terms: 'Bedingungen des Gastgebers',
};
const consentKeysWithoutRecording = ['storage', 'terms'];
/**
 * The items a meeting asks every participant to confirm, in the order of the screen: all four when its event type
 * records (option „Aufzeichnung und Transkript"), else storage and the host's terms only (D-162).
 */
export function meetingConsentKeysFor(recording) {
    return recording ? meetingConsentKeys : consentKeysWithoutRecording;
}
export const MEETING_CONSENT_NOTE = 'Ohne alle vier Bestätigungen kann niemand beitreten. Der Zeitpunkt der Einwilligung wird protokolliert.';
/** The note under the two items of a meeting without recording (D-162). */
export const MEETING_CONSENT_NOTE_WITHOUT_RECORDING = 'Ohne beide Bestätigungen kann niemand beitreten. Der Zeitpunkt der Einwilligung wird protokolliert.';
const consentFlag = z.boolean();
/**
 * Host consent in the lobby: every item the meeting asks for (`meetingConsentKeysFor`) must be true; `recording`
 * and `transcript` are only read when the meeting records.
 */
export const meetingConsentBodySchema = z.object({
    recording: consentFlag.optional(),
    transcript: consentFlag.optional(),
    storage: consentFlag,
    terms: consentFlag,
});
/** Guest consent on `/m/<token>`: the name plus the items the meeting asks for (all of them true). */
export const meetingGuestConsentBodySchema = meetingConsentBodySchema.extend({
    name: z.string().trim().min(1, 'Bitte geben Sie Ihren Namen an.').max(120),
});
export const meetingGuestKeyBodySchema = z.object({ key: z.string().min(20).max(100) });
export const meetingGuestStateQuerySchema = z.object({ key: z.string().min(20).max(100) });
// --- conversation (Beratung 11, 12) --------------------------------------------------------------------------
const dayKey = z.iso.date('Bitte ein Datum im Format JJJJ-MM-TT angeben.');
const title = z.string().trim().min(1, 'Bitte geben Sie einen Titel an.').max(200);
/** „Stelle markieren": a position of the conversation that becomes a task. */
export const meetingMarkerBodySchema = z.object({
    /** Seconds on the conversation clock (recording clock when recorded); default: now − its start. */
    timeSeconds: z.number().int().min(0).max(86_400).optional(),
    /** What was said at that position (optional, shown as the marked line without a transcript). */
    text: z.string().trim().max(2000).optional(),
    taskTitle: title,
    dueOn: dayKey.optional(),
    visibleToCustomer: z.boolean().default(true),
});
/** „Aufgabe anlegen". */
export const meetingTaskBodySchema = z.object({
    title,
    dueOn: dayKey,
    assigneeId: z.string().uuid().optional(),
    visibleToCustomer: z.boolean().default(false),
});
/** Tag „Sichtbar" / „Intern" of a task from the conversation (Beratung 13). */
export const meetingTaskPatchBodySchema = z.object({ visibleToCustomer: z.boolean() });
/** Internal note „In Deal-Timeline speichern" (Beratung 11). */
export const meetingNoteBodySchema = z.object({
    text: z.string().trim().min(1, 'Bitte geben Sie eine Notiz ein.').max(10_000),
});
/** Screen share reports of the client (Beratung 12 „Sie teilen: Grundriss WE 2.05"). */
export const meetingScreenShareBodySchema = z.object({
    state: z.enum(['started', 'ended']),
    label: z.string().trim().min(1).max(200),
});
// --- review (Beratung 13, 15) ----------------------------------------------------------------------------
export const meetingSummaryBodySchema = z.object({ text: z.string().max(20_000) });
export const meetingSummaryApproveBodySchema = z.object({
    text: z.string().trim().min(1, 'Bitte schreiben Sie zuerst eine Zusammenfassung.').max(20_000),
});
//# sourceMappingURL=meetings.js.map